import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Progress } from "../components/ui/progress";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import VoiceDictation from "../components/VoiceDictation";
import PhotoCapture from "../components/PhotoCapture";
import { useLang } from "../contexts/LangContext";
import { toast } from "../components/ui/sonner";
import { queueWrite, isNetworkError } from "../lib/offlineQueue";
import { History, ChevronLeft, ChevronRight, ShieldAlert, CheckCircle2, Lock } from "lucide-react";

const MIN_LABELS = {
  1: { fr: "Ne répond pas aux exigences minimales", en: "Does Not Meet" },
  2: { fr: "Répond partiellement aux exigences minimales", en: "Partially Meets" },
  3: { fr: "Répond pleinement aux exigences minimales", en: "Fully Meets" },
  4: { fr: "Dépasse les exigences minimales", en: "Exceeds" },
};
const WC_LABELS = {
  1: { fr: "Non démarré", en: "Not started" },
  2: { fr: "Partiellement atteint", en: "Partially achieved" },
  3: { fr: "Complètement atteint", en: "Fully achieved" },
  4: { fr: "Mérite une reconnaissance mondiale !", en: "Deserves Global recognition!" },
};
const LEVEL_CLASS = {
  1: "bg-rose-50 border-rose-500 text-rose-700 hover:bg-rose-100 data-[active=true]:bg-rose-600 data-[active=true]:text-white",
  2: "bg-amber-50 border-amber-500 text-amber-800 hover:bg-amber-100 data-[active=true]:bg-amber-500 data-[active=true]:text-white",
  3: "bg-emerald-50 border-emerald-500 text-emerald-800 hover:bg-emerald-100 data-[active=true]:bg-emerald-600 data-[active=true]:text-white",
  4: "bg-sky-50 border-sky-600 text-sky-900 hover:bg-sky-100 data-[active=true]:bg-sky-600 data-[active=true]:text-white",
};
const ENGLISH_MIN = { 1: "1 - Does Not Meet", 2: "2 - Partially Meets", 3: "3 - Fully Meets", 4: "4 - Exceeds" };
const ENGLISH_WC = { 1: "1 - Not started", 2: "2 - Partially achieved", 3: "3 - Fully achieved", 4: "4 - Deserves Global recognition!" };

export default function AuditTerrain() {
  const { auditId } = useParams();
  const navigate = useNavigate();
  const { lang, toggleLang } = useLang();
  const [session, setSession] = useState(null);
  const [index, setIndex] = useState(0);
  const [score, setScore] = useState(null);
  const [constat, setConstat] = useState("");
  const [aRevoir, setARevoir] = useState(false);
  const [motifARevoir, setMotifARevoir] = useState("");
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [savedOffline, setSavedOffline] = useState(false);
  const [hist, setHist] = useState(null);
  const [histOpen, setHistOpen] = useState(false);
  const [actionDialog, setActionDialog] = useState(false);
  const [actionForm, setActionForm] = useState({ description_action: "", responsable: "", echeance: "", priorite: "NORMALE" });
  const [preuves, setPreuves] = useState([]);
  const [validateOpen, setValidateOpen] = useState(false);
  // Latest locally-entered values per requirement_id, set synchronously the instant a save
  // is triggered (before the PUT even starts). The PUT to Google Sheets can take ~2s; if the
  // user navigates away and back to a question before it resolves, the fill-effect below reads
  // from here instead of the (possibly still stale) session data, so a fast Next->Prev can never
  // show/overwrite an empty value while the save is still in flight.
  const pendingRef = useRef({});
  // Per-requirement_id promise chain so two saves for the SAME question (e.g. clicking a score,
  // then blurring the constat moments later) are always sent to the backend one at a time, in
  // order, instead of as two parallel PUTs — this prevents the backend from ever seeing two
  // concurrent "this response doesn't exist yet" checks for the same question.
  const saveQueueRef = useRef({});

  const load = useCallback(() => {
    api.get(`/audits/${auditId}/session`).then((res) => {
      setSession(res.data);
    }).catch((e) => {
      if (!isNetworkError(e)) throw e;
    });
  }, [auditId]);

  useEffect(() => { load(); }, [load]);

  const question = session?.questions?.[index];
  const readOnly = !!session?.read_only;

  useEffect(() => {
    if (!question) return;
    const pending = pendingRef.current[question.requirement_id];
    const resp = question.response;
    setScore(pending ? pending.score : (resp?.score_numeric ? Number(resp.score_numeric) : null));
    setConstat(pending ? pending.constat : (resp?.constat || ""));
    setARevoir(pending ? pending.aRevoir : (resp?.a_revoir === "TRUE"));
    setMotifARevoir(pending ? pending.motifARevoir : (resp?.commentaire_a_revoir || ""));
    setHist(null);
    if (question.response?.response_id) {
      api.get("/preuves", { params: { response_id: question.response.response_id } }).then((res) => setPreuves(res.data));
    } else {
      setPreuves([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, question?.requirement_id]);

  const labels = question?.type_requirement === "WORLD_CLASS" ? WC_LABELS : MIN_LABELS;
  const englishLabels = question?.type_requirement === "WORLD_CLASS" ? ENGLISH_WC : ENGLISH_MIN;

  const save = useCallback((overrides = {}) => {
    if (!question || readOnly) return Promise.resolve();
    const scoreVal = overrides.score ?? score;
    const constatVal = overrides.constat ?? constat;
    const aRevoirVal = overrides.aRevoir ?? aRevoir;
    const motifVal = overrides.motifARevoir ?? motifARevoir;
    // Record synchronously, before the PUT even starts, so a fast navigate-away-and-back
    // always reflects what the user actually entered rather than a stale pre-save value.
    const requirementId = question.requirement_id;
    pendingRef.current[requirementId] = { score: scoreVal, constat: constatVal, aRevoir: aRevoirVal, motifARevoir: motifVal };
    setSaving(true);
    const body = {
      score_numeric: scoreVal,
      compliance_value: englishLabels[scoreVal] || null,
      constat: constatVal,
      a_revoir: aRevoirVal,
      commentaire_a_revoir: motifVal,
    };
    const url = `/audits/${auditId}/reponses/${requirementId}`;
    const doPut = async () => {
      try {
        const res = await api.put(url, body);
        setSavedOffline(false);
        setLastSaved(new Date());
        // Patch the local session state directly with what we just saved instead of
        // re-fetching the whole 62-question session from the server. A re-fetch is
        // async and can be slow/rate-limited, which previously created a race: if the
        // user navigated to another question and back before the re-fetch resolved,
        // the screen could briefly show the OLD (pre-save) response, looking like the
        // answer had "disappeared" even though it was correctly saved server-side.
        setSession((prev) => {
          if (!prev) return prev;
          const current = prev.questions.find((q) => q.requirement_id === requirementId);
          const updatedResponse = {
            ...(current?.response || {}),
            response_id: res.data?.response_id || current?.response?.response_id,
            score_numeric: scoreVal != null ? String(scoreVal) : "",
            compliance_value: body.compliance_value || "",
            constat: constatVal || "",
            a_revoir: aRevoirVal ? "TRUE" : "FALSE",
            commentaire_a_revoir: motifVal || "",
          };
          const questions = prev.questions.map((q) => {
            if (q.requirement_id === requirementId) return { ...q, response: updatedResponse };
            if (q.prerequisites?.some((p) => p.requirement_id === requirementId)) {
              return {
                ...q,
                prerequisites: q.prerequisites.map((p) => (
                  p.requirement_id === requirementId
                    ? { ...p, score_numeric: scoreVal, satisfied: !!scoreVal && scoreVal >= 3 }
                    : p
                )),
              };
            }
            return q;
          });
          const nb_questions_evaluees = questions.filter((q) => q.response?.score_numeric && q.response?.constat).length;
          const nb_a_revoir = questions.filter((q) => q.response?.a_revoir === "TRUE").length;
          return { ...prev, questions, audit: { ...prev.audit, nb_questions_evaluees, nb_a_revoir } };
        });
      } catch (e) {
        if (isNetworkError(e)) {
          await queueWrite({ method: "put", url, data: body });
          setSavedOffline(true);
          setLastSaved(new Date());
          toast.info("Hors-ligne : réponse enregistrée localement, sera synchronisée");
        } else {
          toast.error("Erreur lors de l'enregistrement");
        }
      } finally {
        setSaving(false);
      }
    };
    // Serialize saves per question: chain this one after any save already in flight for the
    // SAME requirement instead of firing both PUTs in parallel (see saveQueueRef comment above).
    const nextChain = (saveQueueRef.current[requirementId] || Promise.resolve()).then(doPut, doPut);
    saveQueueRef.current[requirementId] = nextChain;
    return nextChain;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auditId, question, score, constat, aRevoir, motifARevoir, readOnly]);

  const selectScore = (val) => {
    if (readOnly) return;
    setScore(val);
    save({ score: val });
    if (val < 3) setActionDialog(true);
  };

  const openHistorique = () => {
    api.get(`/audits/${auditId}/historique-2024/${question.requirement_id}`).then((res) => {
      setHist(res.data);
      setHistOpen(true);
    });
  };

  const createAction = async () => {
    try {
      await api.post("/actions", {
        audit_id: auditId, response_id: question.response?.response_id || "", requirement_id: question.requirement_id,
        site_id: session.audit.site_id, reference: question.reference, ...actionForm,
      });
      toast.success("Action corrective créée");
      setActionDialog(false);
      setActionForm({ description_action: "", responsable: "", echeance: "", priorite: "NORMALE" });
    } catch {
      toast.error("Erreur lors de la création de l'action");
    }
  };

  const goto = (i) => {
    if (i >= 0 && i < (session?.questions?.length || 0)) setIndex(i);
  };

  const submitAudit = async () => {
    try {
      await api.post(`/audits/${auditId}/validate`);
      toast.success("Audit validé avec succès");
      setValidateOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Validation impossible");
    }
  };

  const total = session?.questions?.length || 62;
  const evaluated = session?.audit?.nb_questions_evaluees || 0;
  const aRevoirCount = session?.audit?.nb_a_revoir || 0;

  const missingQuestions = useMemo(
    () => (session?.questions || [])
      .map((q, idx) => ({ q, idx }))
      .filter(({ q }) => !q.response || !q.response.score_numeric || !q.response.constat),
    [session],
  );
  const toReviewQuestions = useMemo(
    () => (session?.questions || [])
      .map((q, idx) => ({ q, idx }))
      .filter(({ q }) => q.response?.a_revoir === "TRUE"),
    [session],
  );
  const jumpTo = (idx) => { setIndex(idx); setValidateOpen(false); };

  if (!session || !question) {
    return <div className="p-6 text-sm text-muted-foreground">Chargement de l'audit...</div>;
  }

  return (
    <div className="max-w-2xl mx-auto p-3 sm:p-6 pb-24 space-y-4" data-testid="audit-terrain-page">
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={() => navigate("/audits")}>← Quitter</Button>
        <button data-testid="lang-toggle-audit-button" onClick={toggleLang} className="text-xs font-mono border rounded-full px-3 py-1">
          {lang === "fr" ? "FR" : "EN"} ⇄
        </button>
      </div>

      {readOnly && (
        <div data-testid="audit-readonly-banner" className="flex items-center gap-2 bg-slate-100 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg px-3 py-2 text-xs font-medium text-slate-700 dark:text-slate-300">
          <Lock className="h-3.5 w-3.5" /> Campagne archivée — consultation uniquement, aucune modification possible.
        </div>
      )}

      <div>
        <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
          <span data-testid="audit-question-progress">Question {index + 1} / {total} — {evaluated}/{total} évaluées, {aRevoirCount} à revoir</span>
          <span>{Math.round(((index + 1) / total) * 100)}%</span>
        </div>
        <Progress value={((index + 1) / total) * 100} />
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <Badge data-testid="audit-category-badge" variant="outline">{question.chapitre}{question.sous_chapitre ? ` · ${question.sous_chapitre}` : ""}</Badge>
        <Badge className={question.type_requirement === "WORLD_CLASS" ? "bg-sky-600 text-white" : "bg-slate-700 text-white"}>
          {question.type_requirement === "WORLD_CLASS" ? "World Class" : "Minimum Requirement"}
        </Badge>
        <Badge variant="outline" className="font-mono">Réf. {question.reference}</Badge>
      </div>

      {question.type_requirement === "WORLD_CLASS" && question.prerequisites?.length > 0 && (
        <div className="border border-amber-300 bg-amber-50 dark:bg-amber-950/30 rounded-lg p-3 text-xs space-y-1" data-testid="wc-prerequisites-box">
          <div className="font-semibold flex items-center gap-1"><ShieldAlert className="h-3.5 w-3.5" /> Prérequis Minimum Requirements</div>
          {question.prerequisites.map((p) => (
            <div key={p.requirement_id} className="flex items-center justify-between">
              <span className="font-mono">{p.reference}</span>
              <span className={p.satisfied ? "text-emerald-600 flex items-center gap-1" : "text-rose-600"}>
                {p.satisfied ? <><CheckCircle2 className="h-3 w-3" /> Satisfait</> : `Non satisfait (${p.score_numeric ?? "non évalué"})`}
              </span>
            </div>
          ))}
        </div>
      )}

      <div className="bg-card border rounded-xl p-5">
        <p data-testid="audit-requirement-title" className="font-heading text-xl font-bold leading-snug">
          {lang === "fr" ? question.question_fr : question.question_en}
        </p>
        <p className="text-xs text-muted-foreground mt-2 italic">{lang === "fr" ? question.question_en : question.question_fr}</p>
      </div>

      {question.historique_2024_disponible && !hist && !readOnly && (
        <Button variant="outline" size="sm" className="gap-2" onClick={openHistorique} data-testid="historique-2024-available-button">
          <History className="h-4 w-4" /> Historique 2024 disponible
        </Button>
      )}

      <div className="grid grid-cols-1 gap-2">
        {[1, 2, 3, 4].map((lvl) => (
          <button
            key={lvl}
            data-testid={`score-btn-level-${lvl - 1}`}
            data-active={score === lvl}
            disabled={readOnly}
            onClick={() => selectScore(lvl)}
            className={`score-btn rounded-xl border-2 px-4 py-3 text-left font-semibold transition-colors ${LEVEL_CLASS[lvl]} ${readOnly ? "opacity-70 cursor-not-allowed" : ""}`}
          >
            <div className="text-sm">{lvl} - {labels[lvl][lang]}</div>
          </button>
        ))}
      </div>

      <div className="space-y-2">
        <label className="text-xs font-mono uppercase text-muted-foreground">Constat</label>
        {!readOnly && <VoiceDictation onTranscript={(t) => setConstat(t)} />}
        <Textarea
          data-testid="finding-textarea"
          rows={4}
          value={constat}
          readOnly={readOnly}
          onChange={(e) => setConstat(e.target.value)}
          onBlur={() => save()}
          placeholder="Décrivez votre constat terrain..."
        />
      </div>

      <PhotoCapture
        auditId={auditId}
        siteId={session.audit.site_id}
        responseId={question.response?.response_id}
        requirementId={question.requirement_id}
        existingPhotos={preuves}
        readOnly={readOnly}
        onUploaded={(p) => setPreuves((prev) => [...prev, p])}
        onDeleted={(id) => setPreuves((prev) => prev.filter((p) => p.preuve_id !== id))}
      />

      {!readOnly && (
        <div className="space-y-2 border-t pt-3">
          <button
            data-testid="toggle-to-review-button"
            onClick={() => { const v = !aRevoir; setARevoir(v); save({ aRevoir: v }); }}
            className={`text-sm font-medium px-3 py-2 rounded-md border ${aRevoir ? "bg-purple-100 border-purple-400 text-purple-900" : "border-input"}`}
          >
            📌 {aRevoir ? "Marqué à revoir" : "Marquer à revoir"}
          </button>
          {aRevoir && (
            <Textarea
              data-testid="to-review-reason-textarea"
              placeholder="Pourquoi cette exigence doit-elle être revue ?"
              value={motifARevoir}
              onChange={(e) => setMotifARevoir(e.target.value)}
              onBlur={() => save()}
            />
          )}
        </div>
      )}

      {!readOnly && score && score < 3 && (
        <Button data-testid="add-corrective-action-button" variant="outline" className="w-full border-rose-400 text-rose-700" onClick={() => setActionDialog(true)}>
          Créer une action corrective
        </Button>
      )}

      {!readOnly && (
        <div className="text-[11px] text-muted-foreground text-center" data-testid="audit-save-status">
          {saving ? "Enregistrement..." : lastSaved ? (savedOffline ? `Enregistré hors-ligne à ${lastSaved.toLocaleTimeString()} (en attente de sync)` : `Enregistré à ${lastSaved.toLocaleTimeString()}`) : "Non enregistré"}
        </div>
      )}

      <div className="flex items-center gap-2 sticky bottom-20 md:bottom-4 bg-background/95 backdrop-blur py-2">
        <Button data-testid="audit-prev-question-button" variant="outline" className="flex-1 gap-1" disabled={index === 0} onClick={() => goto(index - 1)}>
          <ChevronLeft className="h-4 w-4" /> Précédent
        </Button>
        {index < total - 1 ? (
          <Button data-testid="audit-next-question-button" className="flex-1 gap-1 bg-emerald-600 hover:bg-emerald-700" onClick={() => goto(index + 1)}>
            Suivant <ChevronRight className="h-4 w-4" />
          </Button>
        ) : !readOnly ? (
          <Button data-testid="audit-open-validation-button" className="flex-1 bg-emerald-600 hover:bg-emerald-700" onClick={() => setValidateOpen(true)}>
            Contrôle final
          </Button>
        ) : null}
      </div>

      <Dialog open={histOpen} onOpenChange={setHistOpen}>
        <DialogContent data-testid="historique-2024-modal">
          <DialogHeader><DialogTitle>Historique 2024</DialogTitle></DialogHeader>
          {hist?.disponible ? (
            <div className="text-sm space-y-2">
              <div>Score 2024 : <b>{hist.reponse_2024.score_numeric || "-"}</b></div>
              <div>Constat 2024 : {hist.reponse_2024.constat || "Non renseigné"}</div>
              {hist.action_2024 && (
                <div className="border-t pt-2 space-y-1">
                  <div>Action 2024 : {hist.action_2024.description_action}</div>
                  <div>Statut : {hist.action_2024.statut_holcim}</div>
                  <div>Responsable : {hist.action_2024.responsable}</div>
                  <div>Échéance : {hist.action_2024.echeance}</div>
                </div>
              )}
            </div>
          ) : <p className="text-sm text-muted-foreground">Aucune donnée 2024 disponible.</p>}
        </DialogContent>
      </Dialog>

      <Dialog open={actionDialog} onOpenChange={setActionDialog}>
        <DialogContent data-testid="create-action-modal">
          <DialogHeader><DialogTitle>Créer une action corrective</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Textarea placeholder="Description de l'action" value={actionForm.description_action} onChange={(e) => setActionForm((f) => ({ ...f, description_action: e.target.value }))} data-testid="action-description-input" />
            <Input placeholder="Responsable" value={actionForm.responsable} onChange={(e) => setActionForm((f) => ({ ...f, responsable: e.target.value }))} data-testid="action-responsable-input" />
            <Input type="date" value={actionForm.echeance} onChange={(e) => setActionForm((f) => ({ ...f, echeance: e.target.value }))} data-testid="action-echeance-input" />
            <Select value={actionForm.priorite} onValueChange={(v) => setActionForm((f) => ({ ...f, priorite: v }))}>
              <SelectTrigger data-testid="action-priorite-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["FAIBLE", "NORMALE", "HAUTE", "CRITIQUE"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActionDialog(false)}>Plus tard</Button>
            <Button data-testid="action-save-button" onClick={createAction} disabled={!actionForm.description_action}>Créer l'action</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={validateOpen} onOpenChange={setValidateOpen}>
        <DialogContent data-testid="validation-control-modal">
          <DialogHeader><DialogTitle>Contrôle final de l'audit</DialogTitle></DialogHeader>
          <div className="text-sm space-y-1">
            <div>{total} exigences</div>
            <div>{evaluated} évaluées</div>
            <div>{aRevoirCount} à revoir</div>
          </div>
          {missingQuestions.length > 0 && (
            <div className="space-y-2 min-w-0" data-testid="validation-missing-list">
              <p className="text-sm font-semibold text-rose-700">
                {missingQuestions.length} exigence(s) non évaluée(s) — cliquez pour y accéder :
              </p>
              <div className="space-y-1.5 max-h-52 overflow-y-auto pr-1">
                {missingQuestions.map(({ q, idx }) => (
                  <button
                    key={q.requirement_id}
                    type="button"
                    data-testid={`validation-jump-missing-${q.requirement_id}`}
                    onClick={() => jumpTo(idx)}
                    className="w-full min-w-0 text-left text-xs px-3 py-2 rounded-md border border-rose-300 bg-rose-50 hover:bg-rose-100 text-rose-800 flex items-center justify-between gap-2"
                  >
                    <span className="truncate min-w-0">Question {idx + 1} — Réf. {q.reference} — {q.chapitre}</span>
                    <ChevronRight className="h-3.5 w-3.5 shrink-0" />
                  </button>
                ))}
              </div>
            </div>
          )}
          {toReviewQuestions.length > 0 && (
            <div className="space-y-2 min-w-0" data-testid="validation-to-review-list">
              <p className="text-sm font-semibold text-purple-700">
                {toReviewQuestions.length} exigence(s) marquée(s) "à revoir" — cliquez pour y accéder :
              </p>
              <div className="space-y-1.5 max-h-52 overflow-y-auto pr-1">
                {toReviewQuestions.map(({ q, idx }) => (
                  <button
                    key={q.requirement_id}
                    type="button"
                    data-testid={`validation-jump-review-${q.requirement_id}`}
                    onClick={() => jumpTo(idx)}
                    className="w-full min-w-0 text-left text-xs px-3 py-2 rounded-md border border-purple-300 bg-purple-50 hover:bg-purple-100 text-purple-800 flex items-center justify-between gap-2"
                  >
                    <span className="truncate min-w-0">Question {idx + 1} — Réf. {q.reference} — {q.chapitre}</span>
                    <ChevronRight className="h-3.5 w-3.5 shrink-0" />
                  </button>
                ))}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setValidateOpen(false)}>Fermer</Button>
            <Button
              data-testid="submit-audit-button"
              className="bg-emerald-600 hover:bg-emerald-700"
              onClick={submitAudit}
              disabled={missingQuestions.length > 0 || toReviewQuestions.length > 0}
            >
              Soumettre l'audit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
